import { Box, Button, Typography } from "@mui/material";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../hooks";
import { toSurfaceTint } from "../../utils/colorManager";
import { SchoolFeeStudentResult } from "../../models/schoolFee";

// Display order is Academic period -> Course -> Level, ALWAYS -- in the result
// list, the summary and the confirmation. It is stated normatively upstream
// (CORE-Service-Listing-API-v1.6.0-final.yaml:101) and it is the order a
// parent reads a placement in, whatever the labels happen to say.
const placementRows = (
  enrolment: SchoolFeeStudentResult["enrolments"][number],
  labels: { period: string; course: string; level: string }
) => [
  { title: labels.period, value: enrolment.period.label },
  { title: labels.course, value: enrolment.course.name },
  { title: labels.level, value: enrolment.level.name },
];

// A student whose every fee is paid has nothing this journey can take.
const owesNothing = (hit: SchoolFeeStudentResult) =>
  hit.enrolments.every(enrolment => enrolment.fees.length === 0);

// The answer to the search can render below the fold on a phone; it is
// brought into view so the payer is not left reading the search form. Said on
// the brand's own tinted surface, as the dummy draws its info banner.
const NothingToPay = ({ text }: { text: string }) => {
  const { theme } = useTheme();
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    ref.current?.scrollIntoView({ block: "nearest" });
  }, []);
  return (
    <Typography
      ref={ref}
      role="status"
      sx={{
        display: "block",
        fontSize: 14,
        fontWeight: 600,
        color: "primary.main",
        bgcolor: toSurfaceTint(theme.color_secondary),
        borderRadius: 2,
        px: 1.5,
        py: 1.25,
      }}
    >
      {text}
    </Typography>
  );
};

const fullName = (student: SchoolFeeStudentResult["student"]) =>
  `${student.first_name} ${student.last_name}`.trim();

/**
 * The students a search returned, drawn as the click dummy's result card: the
 * name with its matricule in a pill, the placement as three labelled values,
 * and one full-width button to continue.
 *
 * One hit is still a list: selecting is what carries the identity into the
 * journey, so a single result is confirmed by the payer rather than assumed
 * for them -- and it alone gets the brand-bordered card, because it is the
 * only answer rather than one of several.
 */
const StudentResultList = ({
  results,
  onSelect,
}: {
  results: SchoolFeeStudentResult[];
  onSelect: (hit: SchoolFeeStudentResult) => void;
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const primary = theme.color_primary;
  const multiple = results.length > 1;

  const labels = {
    period: t("schoolfee.student.period"),
    course: t("schoolfee.student.course"),
    level: t("schoolfee.student.level"),
  };

  return (
    <Box mt={3}>
      <Box mb={2}>
        <Typography sx={{ fontSize: 16, fontWeight: 600, lineHeight: 1.3 }}>
          {t(
            multiple
              ? "schoolfee.search.multiple_title"
              : "schoolfee.search.found_title"
          )}
        </Typography>
        <Typography sx={{ fontSize: 14, color: "text.secondary", mt: 0.5 }}>
          {multiple
            ? t("schoolfee.search.multiple_count", { count: results.length })
            : t("schoolfee.search.found_hint")}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {results.map(hit => (
          <Box
            key={hit.student.student_uuid}
            sx={{
              display: "grid",
              gap: 1.5,
              p: 2,
              borderRadius: 3,
              border: 1,
              bgcolor: "background.paper",
              // The single answer is framed in the brand colour, border and
              // ring, as the dummy draws `.pf-res.one`; several answers stay
              // a plain list to choose from.
              borderColor: multiple ? "divider" : primary,
              boxShadow: multiple ? "none" : `inset 0 0 0 1px ${primary}`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px 12px",
                flexWrap: "wrap",
              }}
            >
              <Typography component="span" sx={{ fontSize: 16, fontWeight: 600 }}>
                {fullName(hit.student)}
              </Typography>
              <Box
                component="span"
                sx={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  bgcolor: "background.default",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: "999px",
                  px: 1.25,
                  py: "2px",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {hit.student.matricule}
              </Box>
            </Box>

            {hit.enrolments.map(enrolment => (
              <Box
                key={enrolment.period.period_uuid}
                component="dl"
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr 1fr",
                    sm: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: "10px 18px",
                  m: 0,
                }}
              >
                {placementRows(enrolment, labels).map(row => (
                  <Box key={row.title} sx={{ minWidth: 0 }}>
                    <Typography
                      component="dt"
                      sx={{
                        fontSize: 11.5,
                        fontWeight: 700,
                        letterSpacing: ".07em",
                        textTransform: "uppercase",
                        color: "text.secondary",
                      }}
                    >
                      {row.title}
                    </Typography>
                    <Typography
                      component="dd"
                      sx={{ m: 0, fontWeight: 600, overflowWrap: "anywhere" }}
                    >
                      {row.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ))}

            {owesNothing(hit) ? (
              <NothingToPay text={t("schoolfee.search.nothing_to_pay")} />
            ) : multiple ? (
              // The visible word is short; the accessible name says which
              // student, so two similar names are never an unlabelled choice.
              <Box>
                <Button
                  variant="contained"
                  onClick={() => onSelect(hit)}
                  aria-label={t("schoolfee.search.select", {
                    name: fullName(hit.student),
                  })}
                  sx={{ textTransform: "none", height: 46, borderRadius: 2.5 }}
                >
                  {t("schoolfee.search.select_short")}
                </Button>
              </Box>
            ) : (
              <Button
                variant="contained"
                fullWidth
                onClick={() => onSelect(hit)}
                sx={{ textTransform: "none", height: 46, borderRadius: 2.5 }}
              >
                {t("schoolfee.search.continue_with", {
                  name: hit.student.first_name,
                })}
              </Button>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default StudentResultList;
